/**
 * WS2812 RGB Panel Component
 * 256-color palette matching the original Python implementation
 */

import { serial } from '../serial/serial.js';
import { WS2812_MAX_PIXELS, validateWS2812Hex } from '../serial/protocol.js';
import { notifications } from './notifications.js';
import { t } from '../utils/i18n.js';

// Generate 256-color palette
function generatePalette() {
    const palette = [];

    // 00-0F: Basic colors (16) - matching Python palette order
    const vgaColors = [
        [0, 0, 0],       // 00: Black
        [128, 0, 0],     // 01: Maroon (Dark Red)
        [0, 128, 0],     // 02: Green
        [128, 128, 0],   // 03: Olive
        [0, 0, 128],     // 04: Navy (Dark Blue)
        [128, 0, 128],   // 05: Purple
        [0, 128, 128],   // 06: Teal
        [192, 192, 192], // 07: Silver
        [128, 128, 128], // 08: Gray
        [255, 0, 0],     // 09: Red
        [0, 255, 0],     // 0A: Lime
        [255, 255, 0],   // 0B: Yellow
        [0, 0, 255],     // 0C: Blue
        [255, 0, 255],   // 0D: Magenta
        [0, 255, 255],   // 0E: Cyan
        [255, 255, 255]  // 0F: White
    ];

    for (const [r, g, b] of vgaColors) {
        palette.push({ r, g, b });
    }

    // 10-E7: 6×6×6 RGB cube (216)
    // Each component: 0, 51, 102, 153, 204, 255
    const cubeValues = [0, 51, 102, 153, 204, 255];
    for (let r = 0; r < 6; r++) {
        for (let g = 0; g < 6; g++) {
            for (let b = 0; b < 6; b++) {
                palette.push({
                    r: cubeValues[r],
                    g: cubeValues[g],
                    b: cubeValues[b]
                });
            }
        }
    }

    // E8-FF: Grayscale (24)
    // Values: 8 to 238 in steps of 10
    for (let i = 0; i < 24; i++) {
        const gray = 8 + i * 10;
        palette.push({ r: gray, g: gray, b: gray });
    }

    return palette;
}

export class WS2812Panel {
    constructor(pinsPanel) {
        this._pinsPanel = pinsPanel;
        this._paletteContainer = document.getElementById('ws-palette');
        this._previewContainer = document.getElementById('ws-led-preview');
        this._hexInput = document.getElementById('ws-hex-input');
        this._ledCountInput = document.getElementById('ws-led-count');

        this._palette = generatePalette();
        this._selectedColor = 0;
        this._ledColors = [];
        this._ledCount = 8;

        this._renderPalette();
        this._renderPreview();
        this._bindEvents();
    }

    _renderPalette() {
        let html = '';
        for (let i = 0; i < this._palette.length; i++) {
            const { r, g, b } = this._palette[i];
            const hex = i.toString(16).padStart(2, '0').toUpperCase();
            html += `
                <div class="palette-color"
                     data-index="${i}"
                     data-hex="${hex}"
                     style="background-color: rgb(${r}, ${g}, ${b});"
                     title="${hex}: RGB(${r}, ${g}, ${b})">
                </div>
            `;
        }
        this._paletteContainer.innerHTML = html;
    }

    _renderPreview() {
        const parsedCount = Number(this._ledCountInput?.value);
        const count = Number.isInteger(parsedCount) ? parsedCount : 8;
        this._ledCount = Math.min(Math.max(count, 1), WS2812_MAX_PIXELS);
        if (this._ledCountInput) this._ledCountInput.value = this._ledCount;

        let html = '';
        for (let i = 0; i < this._ledCount; i++) {
            const color = this._ledColors[i];
            let style = '';
            let className = 'led';

            if (color !== undefined && color !== null) {
                const { r, g, b } = this._palette[color];
                if (r > 0 || g > 0 || b > 0) {
                    style = `background-color: rgb(${r}, ${g}, ${b}); color: rgb(${r}, ${g}, ${b});`;
                    className = 'led on';
                }
            }

            html += `<div class="${className}" data-index="${i}" style="${style}"></div>`;
        }
        this._previewContainer.innerHTML = html;
    }

    _bindEvents() {
        // Palette click
        this._paletteContainer.addEventListener('click', (e) => {
            const colorEl = e.target.closest('.palette-color');
            if (colorEl) {
                const index = parseInt(colorEl.dataset.index);
                this._selectColor(index);
            }
        });

        // Preview LED click - set color
        this._previewContainer.addEventListener('click', (e) => {
            const ledEl = e.target.closest('.led');
            if (ledEl) {
                const index = parseInt(ledEl.dataset.index);
                this._setLedColor(index, this._selectedColor);
            }
        });

        // Quick action buttons
        document.querySelectorAll('.btn-ws[data-color]').forEach(btn => {
            btn.addEventListener('click', () => {
                const colorHex = btn.dataset.color;
                const colorIndex = parseInt(colorHex, 16);
                this._fillAll(colorIndex);
            });
        });

        // Rainbow effect
        document.querySelector('.btn-ws[data-effect="rainbow"]')?.addEventListener('click', () => {
            this._applyRainbow();
        });

        // Send button
        document.getElementById('btn-ws-send')?.addEventListener('click', () => {
            this._sendHex();
        });

        // LED count change
        this._ledCountInput?.addEventListener('change', () => {
            this._renderPreview();
        });

        // Hex input enter
        this._hexInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                this._sendHex();
            }
        });
    }

    _selectColor(index) {
        // Remove previous selection
        this._paletteContainer.querySelectorAll('.palette-color.selected').forEach(el => {
            el.classList.remove('selected');
        });

        // Add selection
        const colorEl = this._paletteContainer.querySelector(`[data-index="${index}"]`);
        if (colorEl) {
            colorEl.classList.add('selected');
        }

        this._selectedColor = index;
    }

    _setLedColor(ledIndex, colorIndex) {
        this._ledColors[ledIndex] = colorIndex;
        this._renderPreview();
        this._updateHexInput();
    }

    _fillAll(colorIndex) {
        for (let i = 0; i < this._ledCount; i++) {
            this._ledColors[i] = colorIndex;
        }
        this._renderPreview();
        this._updateHexInput();
        this._sendCurrentColors();
    }

    _applyRainbow() {
        // Rainbow using RGB cube colors
        const rainbowColors = [
            0x09, // Red
            0x1B, // Orange (approx)
            0x0B, // Yellow
            0x0A, // Lime/Green
            0x0E, // Cyan
            0x0C, // Blue
            0x0D, // Magenta
        ];

        for (let i = 0; i < this._ledCount; i++) {
            this._ledColors[i] = rainbowColors[i % rainbowColors.length];
        }
        this._renderPreview();
        this._updateHexInput();
        this._sendCurrentColors();
    }

    _updateHexInput() {
        let hex = '';
        for (let i = 0; i < this._ledCount; i++) {
            const colorIndex = this._ledColors[i] || 0;
            hex += colorIndex.toString(16).padStart(2, '0').toUpperCase();
        }
        this._hexInput.value = hex;
    }

    async _sendHex() {
        let hex;
        try {
            hex = validateWS2812Hex(this._hexInput.value);
        } catch (error) {
            notifications.error(error.message || t('invalid-value'));
            return;
        }

        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        if (!this._ensureWS2812Mode()) {
            return;
        }

        try {
            const success = await serial.sendWS2812(hex);
            if (success) {
                // Update preview from hex
                this._ledColors = [];
                for (let i = 0; i < hex.length; i += 2) {
                    const colorIndex = parseInt(hex.substr(i, 2), 16);
                    this._ledColors.push(colorIndex);
                }
                this._ledCount = this._ledColors.length;
                this._ledCountInput.value = this._ledCount;
                this._renderPreview();
            } else {
                notifications.error(t('command-error'));
            }
        } catch (error) {
            notifications.error(error.message);
        }
    }

    async _sendCurrentColors() {
        if (!serial.connected) return;
        if (!this._ensureWS2812Mode()) return;

        let hex = '';
        for (let i = 0; i < this._ledCount; i++) {
            const colorIndex = this._ledColors[i] || 0;
            hex += colorIndex.toString(16).padStart(2, '0').toUpperCase();
        }

        try {
            await serial.sendWS2812(hex);
        } catch (error) {
            notifications.error(error.message);
        }
    }

    _ensureWS2812Mode() {
        if (!this._pinsPanel) {
            return true;
        }

        if (this._pinsPanel.modes[3] !== 'W') {
            notifications.warning(t('ws2812-mode-required'));
            return false;
        }

        return true;
    }

    get palette() {
        return this._palette;
    }
}
